/**
 * Atom: MortiseMark + MortiseWordmark
 *
 * Brand mark for Tenon. Colors use CSS custom properties so they flip
 * automatically with [data-theme="dark|light"] on the root element.
 * R1.1 compliant — no raw hex. var(--brand-gold) resolves to the
 * canonical palette: #C8B75D dark / #A8862C light.
 *
 * Usage:
 *   <MortiseMark size={26} />
 *   <MortiseWordmark size={24} />
 */
import React from 'react'

interface MortiseMarkProps {
  size?: number
  /** Force light-theme tint (wraps in data-theme="light" so CSS vars resolve
   *  to the light palette — use on surfaces that are always light). */
  light?: boolean
  className?: string
  style?: React.CSSProperties
}

export const MortiseMark: React.FC<MortiseMarkProps> = ({ size = 28, light, className, style }) => {
  const svg = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      style={{ display: 'block', ...style }}
      className={className}
      aria-label="Tenon"
      role="img"
    >
      <rect x="6" y="14" width="36" height="36" rx="2" fill="none" stroke="var(--brand-gold)" strokeWidth="3" />
      <rect x="32" y="22" width="26" height="20" fill="var(--brand-gold)" />
      <line x1="42" y1="22" x2="42" y2="42" stroke="var(--brand-gold-ink)" strokeWidth="1.5" />
    </svg>
  )
  if (light) return <span data-theme="light" style={{ display: 'contents' }}>{svg}</span>
  return svg
}

interface MortiseWordmarkProps {
  size?: number
  /** Force light-theme tint — see MortiseMark. */
  light?: boolean
  className?: string
  style?: React.CSSProperties
}

export const MortiseWordmark: React.FC<MortiseWordmarkProps> = ({ size = 24, light, className, style }) => {
  return (
    <div
      data-theme={light ? 'light' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: size * 0.32,
        ...style,
      }}
      className={className}
      aria-label="Tenon"
    >
      <MortiseMark size={size * 1.6} />
      <span
        style={{
          font: `700 ${size * 1.25}px/1 Karla, sans-serif`,
          letterSpacing: '-0.04em',
          color: 'var(--text-primary)',
        }}
      >
        Tenon
      </span>
    </div>
  )
}
